import React, { useState } from "react";
import { Tasks } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Pause, Play } from "lucide-react";
import { toast } from "sonner";

/**
 * Manual SLA Pause / Resume control. Visible only while task is pending and
 * not already on transit hold. Pausing requires remarks; resume is one click.
 */
export default function SlaPauseButton({ task, onChange }) {
  const [open, setOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  if (!task || task.status === "completed") return null;
  if (!task.sla?.budget_minutes) return null;        // open-ended task — no SLA
  if (task.sla?.sale_on_hold) return null;           // transit hold beats manual pause

  const paused = !!task.sla_paused;

  const submitPause = async () => {
    if (!remarks.trim()) {
      toast.error("Remarks are required to pause SLA");
      return;
    }
    setSaving(true);
    try {
      await Tasks.slaPause(task.id, { remarks: remarks.trim() });
      toast.success("SLA paused");
      setOpen(false);
      setRemarks("");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to pause SLA");
    } finally { setSaving(false); }
  };

  const submitResume = async () => {
    setSaving(true);
    try {
      await Tasks.slaResume(task.id);
      toast.success("SLA resumed");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resume SLA");
    } finally { setSaving(false); }
  };

  if (paused) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={submitResume}
        disabled={saving}
        data-testid={`sla-resume-${task.type}`}
        className="gap-1"
      >
        <Play className="h-3.5 w-3.5" /> Resume SLA
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`sla-pause-${task.type}`}
          className="gap-1"
        >
          <Pause className="h-3.5 w-3.5" /> Pause SLA
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pause SLA Timer</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Pausing the SLA freezes business-hour countdown until you resume it.
          Use this when you're blocked waiting on the customer or an external party.
        </p>
        <Textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={3}
          placeholder="Reason (e.g., Awaiting RC copy from customer)"
          data-testid="sla-pause-remarks"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submitPause} disabled={saving} data-testid="sla-pause-confirm">
            Pause SLA
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
